import { useEffect, useState } from "react";
import { X, Loader2, CheckCircle2, BadgeCheck, Ticket, QrCode, CreditCard, Sparkles } from "lucide-react";
import QRCode from "qrcode";
import { api } from "../lib/api";
import { useEscapeKey } from "../lib/useEscapeKey";

/**
 * v3.9.34 — posting paywall (CEO #1), customer side. Shown when a user has used
 * their free post in a category and posting is enabled. Two ways forward:
 *   • Subscribe (recurring) — unlimited posting while active (usually the better value)
 *   • Buy one extra post (one-time)
 * Both pay via GMPay (QRPH or Card), reusing the wallet cash-in rails. Because GMPay
 * settles asynchronously, after payment we poll entitlement and, once granted, call
 * onEntitled() so the caller can finish publishing.
 */
type Fees = { perPost: number; subscription: number; subscriptionDays: number };
import { formatPhp } from "../lib/money";
const peso = formatPhp;

export function PaywallModal({
  open, onClose, fees, postType, category, onEntitled,
}: {
  open: boolean;
  onClose: () => void;
  fees: Fees;
  postType: string;
  category: string;
  onEntitled: () => void;
}) {
  const [method, setMethod] = useState<"qrph" | "card">("qrph");
  const [busy, setBusy] = useState<null | "subscription" | "post_credit">(null);
  const [err, setErr] = useState<string | null>(null);
  const [qrImg, setQrImg] = useState<string | null>(null);
  const [pending, setPending] = useState<null | { kind: string; text: string; url?: string }>(null);
  const [checking, setChecking] = useState(false);
  useEscapeKey(onClose, open && !busy && !checking);

  useEffect(() => { if (open) { setBusy(null); setErr(null); setQrImg(null); setPending(null); setChecking(false); } }, [open]);

  // While a payment is pending, poll entitlement every few seconds; proceed once granted.
  useEffect(() => {
    if (!open || !pending) return;
    let alive = true;
    const t = setInterval(async () => {
      const st = await api.billingStatus(postType, category).catch(() => null);
      if (!alive) return;
      if (st && st.allowed) { clearInterval(t); onEntitled(); }
    }, 4000);
    return () => { alive = false; clearInterval(t); };
  }, [open, pending, postType, category, onEntitled]);

  if (!open) return null;

  async function pay(kind: "subscription" | "post_credit") {
    setBusy(kind); setErr(null); setQrImg(null); setPending(null);
    try {
      const r = await api.billingPay({ kind, method, postType, category });
      if (r?.status === "completed") { onEntitled(); return; }
      // pending — show QR or open the card checkout, then poll for settlement.
      if (method === "qrph" && r?.qrRaw) {
        const img = await QRCode.toDataURL(r.qrRaw, { width: 220, margin: 1, errorCorrectionLevel: "H" });
        setQrImg(img);
        setPending({ kind, text: "Scan this QRPH code to pay. Your post unlocks once GMPay confirms." });
      } else if (method === "card" && r?.checkoutUrl) {
        setPending({ kind, text: "Complete your card payment with GMPay. Your post unlocks once confirmed.", url: r.checkoutUrl });
        try { window.open(r.checkoutUrl, "_blank", "noopener"); } catch { /* ignore */ }
      } else {
        setPending({ kind, text: "Payment started — your post unlocks once GMPay confirms." });
      }
    } catch (failure: unknown) {
      // [plans.atomic-publication](../../../docs/product/plans-and-referrals.md#plans-atomic-publication): a stale paywall can resume its retained draft when posting becomes free.
      if (failure instanceof Error && "code" in failure &&
          (failure.code === "paid_plans_unavailable" || failure.code === "posting_fees_disabled")) {
        onEntitled();
      } else setErr(failure instanceof Error ? failure.message : "Could not start the payment.");
    }
    finally { setBusy(null); }
  }

  async function checkNow() {
    setChecking(true);
    try {
      const st = await api.billingStatus(postType, category).catch(() => null);
      if (st && st.allowed) { onEntitled(); return; }
      setErr("We haven't seen the payment settle yet. If you've just paid, give it a few seconds and check again.");
    } finally { setChecking(false); }
  }

  const days = fees.subscriptionDays || 30;
  const perPeriod = days % 30 === 0 ? `${days / 30} mo` : `${days} days`;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className="card w-full max-w-lg p-0 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-line flex items-center gap-2">
          <Ticket className="w-4 h-4 text-navy" />
          <h3 className="font-bold text-navy">One more step to publish</h3>
          <button onClick={onClose} aria-label="Close" className="btn-ghost h-8 w-8 p-0 ml-auto"><X className="w-4 h-4" /></button>
        </div>

        {pending ? (
          <div className="p-6 text-center">
            {qrImg ? <img src={qrImg} alt="GMPay QRPH" className="w-52 h-52 mx-auto rounded-lg border border-line" />
              : <div className="w-14 h-14 rounded-full bg-brand-gold/10 grid place-items-center mx-auto mb-2"><Loader2 className="w-7 h-7 text-brand-gold animate-spin" /></div>}
            <p className="text-sm text-mut mt-3">{pending.text}</p>
            {pending.url && <a href={pending.url} target="_blank" rel="noopener noreferrer" className="text-sm text-brand-red underline">Reopen payment page</a>}
            {err && <p className="text-xs text-brand-red mt-2">{err}</p>}
            <div className="flex gap-2 justify-center mt-4">
              <button onClick={checkNow} disabled={checking} className="btn-navy h-9 px-4 text-sm disabled:opacity-60">{checking ? <Loader2 className="w-4 h-4 animate-spin" /> : "I've paid — check now"}</button>
              <button onClick={onClose} className="btn-outline h-9 px-4 text-sm">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            <p className="text-sm text-mut">You've used your free post in <b className="text-navy">{category}</b> for this type. Subscribe for unlimited posting, or pay for just this one.</p>

            {/* Subscribe — best value */}
            <button onClick={() => pay("subscription")} disabled={!!busy}
              className="w-full text-left rounded-xl border-2 border-brand-green/40 bg-brand-green/5 p-4 hover:border-brand-green transition disabled:opacity-60">
              <div className="flex items-center gap-2">
                <BadgeCheck className="w-5 h-5 text-brand-green" />
                <span className="font-bold text-navy">Subscribe — unlimited posting</span>
                <span className="chip bg-brand-green/15 text-brand-greendark ml-auto flex items-center gap-1"><Sparkles className="w-3 h-3" />Best value</span>
              </div>
              <div className="mt-1 text-2xl font-extrabold text-navy">{peso(fees.subscription)}<span className="text-sm font-semibold text-mut"> / {perPeriod}</span></div>
              <p className="text-xs text-mut mt-1">Post as much as you want in every category while active. {busy === "subscription" && "Starting…"}</p>
            </button>

            {/* Single post */}
            <button onClick={() => pay("post_credit")} disabled={!!busy}
              className="w-full text-left rounded-xl border border-line p-4 hover:border-navy-500 transition disabled:opacity-60">
              <div className="flex items-center gap-2">
                <Ticket className="w-5 h-5 text-navy" />
                <span className="font-bold text-navy">Buy just this post</span>
                <span className="ml-auto text-2xl font-extrabold text-navy">{peso(fees.perPost)}</span>
              </div>
              <p className="text-xs text-mut mt-1">A one-time charge that unlocks this single extra post. {busy === "post_credit" && "Starting…"}</p>
            </button>

            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-mut">Pay with</span>
                <button onClick={() => setMethod("qrph")} className={`chip flex items-center gap-1 ${method === "qrph" ? "bg-navy text-white" : "bg-navy-50 text-navy-600"}`}><QrCode className="w-3 h-3" />QRPH</button>
                <button onClick={() => setMethod("card")} className={`chip flex items-center gap-1 ${method === "card" ? "bg-navy text-white" : "bg-navy-50 text-navy-600"}`}><CreditCard className="w-3 h-3" />Card</button>
              </div>
              <span className="text-[11px] text-mut flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-brand-green" />Secured by GMPay</span>
            </div>
            {err && <p className="text-xs text-brand-red">{err}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
